/**
 * How a claim ended, in one vocabulary.
 *
 * "Dead end" used to cover four opposite outcomes. An objection that knocked its
 * parent down read the same as a path the argument ran out of, and both read the
 * same as a scope question only the user could answer — which the coach then
 * offered for pruning. A reader could not tell a finding against the parent from
 * a question waiting on them, so the one word was deciding nothing.
 *
 * Four endings, each named for what the RECORD says and no more:
 *
 *  - `objection-stood` — a terminal contradiction. The model raised an objection
 *    and closed the path on it, so the objection held against its parent in
 *    reasoning. Never "refuted": that word belongs to the citation gate
 *    (`refutedIsEarned`) and to a result somebody recorded, and an argument the
 *    model had with itself is neither.
 *  - `open` — a terminal knowledge gap. Reasoning stopped because reasoning
 *    cannot settle it; it needs a person, a definition or the world. It is the
 *    opposite of clutter, which is why `prune` never offers one.
 *  - `path-failed` — any other terminal node: the argument ran out.
 *  - `answered` — a recorded result went one way or the other. It outranks the
 *    three above, exactly as `channelOf` lets an outcome outrank `terminal`: the
 *    model's judgement that a path is exhausted is not allowed to talk over an
 *    observation. An inconclusive result answers nothing, so it falls through.
 *
 * One runtime import, and not one that can close a cycle — which matters, since
 * the card, the feed, the legend, the coach and every export read this. The WORDS
 * for the endings are status words and live in `core/vocabulary.ts` with every
 * other one, which imports only the money formatter at runtime, and that imports
 * nothing. This module decides which ending a node reached; the table says what
 * it is called. The words are re-exported here, so every existing import works.
 */

import type { Directive, ReasonNode, ReasonedEndingCounts, Session } from './types'
import { ENDING_LABEL, OBJECTION_STOOD_BELOW, endingCount } from './vocabulary'

export { ENDING_LABEL, OBJECTION_STOOD_BELOW, endingCount }

export type Ending = 'objection-stood' | 'open' | 'path-failed' | 'answered'

/**
 * The ending a node has reached, or null while it is still open.
 *
 * Read off stored fields only — `outcome.bearing`, `terminal` and `kind` — so any
 * surface holding the node words it the same way.
 */
export function endingOf(n: ReasonNode): Ending | null {
  if (n.outcome && n.outcome.bearing !== 'inconclusive') return 'answered'
  return reasonedEndingOf(n)
}

/**
 * The ending the MODEL gave a closed path, ignoring any result recorded since.
 *
 * For the one place that has to print both: the reason the model closed a path
 * stays on the record after somebody answers it, and a document that printed
 * that reason under "answered by your result" would put the model's words in
 * the user's mouth.
 */
export function reasonedEndingOf(n: ReasonNode): Exclude<Ending, 'answered'> | null {
  if (!n.terminal) return null
  if (n.kind === 'contradiction') return 'objection-stood'
  if (n.kind === 'gap') return 'open'
  return 'path-failed'
}

/**
 * A reasoned ending as the MODEL is shown it — "objection stood (in reasoning)",
 * "path failed (in reasoning)", "open: reasoning can't settle it" — or null when
 * the node is not terminal. The model's own verdict on its path, so it names the
 * reasoned ending even once a result has been recorded; every prompt that prints
 * this also prints the result beside it. One phrase, so the voice, Ask and the
 * reasoning passes are told the same words the card shows. Never "closed": a
 * live measurement found the voice counting an open question among the closed
 * paths when the mark called it "closed in reasoning: open".
 */
export function closedMark(n: ReasonNode): string | null {
  const e = reasonedEndingOf(n)
  if (!e) return null
  return e === 'open' ? ENDING_LABEL.open : `${ENDING_LABEL[e]} (in reasoning)`
}

/** The endings the model REASONED to among `nodes` — "2 objections stood, 1 open:
 *  reasoning can't settle it" — ignoring any result recorded since, or '' when
 *  none has one. The count a prompt reads beside `closedMark`. */
export function reasonedEndingCounts(nodes: readonly ReasonNode[]): string {
  return endingTally(countEndings(nodes, reasonedEndingOf))
}

/**
 * The standing-brief directive a person wrote to answer this node, if any — the
 * gap's "Answer it" (`Directive.answers`). A question reasoning cannot settle is
 * waiting on the user, and a scope question ("'displace' is underspecified") is
 * one only they can answer; the brief is where their answer already had a home,
 * a given every later pass reasons inside and never certifies. Read off the
 * brief rather than stored on the node, so taking the directive out of the brief
 * — its chip's ✕, or an undo — is also what re-opens the gap.
 */
export function briefAnswerOf(s: Pick<Session, 'brief'>, id: string): Directive | undefined {
  return s.brief?.find((d) => d.answers === id)
}

/** The label for a node's ending, or null while it has none. */
export function endingLabelOf(n: ReasonNode): string | null {
  const e = endingOf(n)
  return e ? ENDING_LABEL[e] : null
}

/**
 * The objections that stood directly under a node — its children whose ending is
 * `objection-stood`, in arrival order. An objection somebody then answered with
 * a result has an ending of its own, and "stood, in reasoning" would understate
 * it, so it is not counted here.
 */
export function objectionsStoodUnder(s: Session, id: string): ReasonNode[] {
  const out: ReasonNode[] = []
  for (const cid of s.order) {
    const c = s.nodes[cid]
    if (c && c.parentId === id && endingOf(c) === 'objection-stood') out.push(c)
  }
  return out
}

let stoodMemo: { nodes: Session['nodes']; ids: ReadonlySet<string> } | null = null

/**
 * Every claim an objection below it stood against: the parents
 * `objectionsStoodUnder` would name something for, all at once.
 *
 * For the surfaces that ask about every node together — both renderers per
 * frame, the captions, the node tree — so it is one walk per change to the
 * graph, memoised on the identity of `nodes`, which the store replaces on every
 * change (`taintMap`'s arrangement). The seed is in it when an objection stood
 * against the question itself; a surface decides whether it has somewhere to
 * say so.
 */
export function stoodAgainst(s: Pick<Session, 'nodes'>): ReadonlySet<string> {
  if (stoodMemo?.nodes === s.nodes) return stoodMemo.ids
  const ids = new Set<string>()
  for (const n of Object.values(s.nodes)) {
    if (n.parentId !== null && endingOf(n) === 'objection-stood') ids.add(n.parentId)
  }
  stoodMemo = { nodes: s.nodes, ids }
  return ids
}

/** The order endings are counted in: what the argument closed, then what the
 *  world answered. */
export const ENDING_ORDER: readonly Ending[] = [
  'objection-stood',
  'path-failed',
  'open',
  'answered',
]

/** Every ending among `nodes`, counted and joined — "2 objections stood, 1 path
 *  failed" — or the empty string when none of them has one. */
export function endingSummary(nodes: readonly ReasonNode[]): string {
  return endingTally(countEndings(nodes, endingOf))
}

/** How many of `nodes` reached each ending `of` gives them. */
function countEndings(
  nodes: readonly ReasonNode[],
  of: (n: ReasonNode) => Ending | null,
): Partial<Record<Ending, number>> {
  const counts: Partial<Record<Ending, number>> = {}
  for (const n of nodes) {
    const e = of(n)
    if (e) counts[e] = (counts[e] ?? 0) + 1
  }
  return counts
}

/**
 * Counts of endings, in the endings' own words and order — "1 objection stood,
 * 1 open: reasoning can't settle it" — or '' when every count is zero. The one
 * place a tally becomes words: the graph's counts above and a run's stored ones
 * below both come here, so no surface can count an ending one way and word it
 * another.
 */
export function endingTally(counts: Partial<Record<Ending, number>>): string {
  return ENDING_ORDER.filter((e) => (counts[e] ?? 0) > 0)
    .map((e) => endingCount(e, counts[e]!))
    .join(', ')
}

/** A run's count said whole: true of every ending, and the model's. */
export const RUN_ENDINGS_WHOLE = "reached an ending in the model's reasoning"

/**
 * What a run's closed paths came to, in the endings' own words, or null for
 * none. Two lengths of one line:
 *
 *  - `account` (the Runs tab, the export) — "3 reached an ending in the model's
 *    reasoning — 1 objection stood, 1 path failed, 1 open: reasoning can't settle
 *    it": the total, whose it is, and what it came to;
 *  - `strip` (the run strip, live) — the endings alone, "1 objection stood, 1
 *    path failed, 1 open: reasoning can't settle it".
 *
 * Every surface used to print `dead` as "dead ends", and `dead` counts every
 * node the model marked terminal — the question reasoning cannot settle as well,
 * which is the one ending that is not a dead end at all: it waits on a person, a
 * definition or the world, and prune never offers it. So the count is split by
 * the tally the run stored as its nodes landed (`Run.endings`) and worded by
 * `endingTally`, the one arithmetic the graph's own counts use. A run stored
 * before that tally existed — or one whose tally does not add up to its own
 * count — says the total alone at both lengths, which is true of every ending
 * and splits nothing by a guess.
 */
export function runEndingsLine(
  r: { dead: number; endings?: ReasonedEndingCounts },
  length: 'account' | 'strip' = 'account',
): string | null {
  if (r.dead <= 0) return null
  const whole = `${r.dead} ${RUN_ENDINGS_WHOLE}`
  const e = r.endings
  const sum = e ? (e['objection-stood'] ?? 0) + (e['path-failed'] ?? 0) + (e.open ?? 0) : 0
  if (!e || sum !== r.dead) return whole
  return length === 'strip' ? endingTally(e) : `${whole} — ${endingTally(e)}`
}

/**
 * What a first-ring ending means under each frame, for the line under a title.
 * One table so the markdown documents and the PDF cannot word the frame two ways.
 */
export const FRAME_NOTE: Record<'question' | 'goal', string> = {
  question:
    'Explored as a QUESTION: first-ring branches are competing candidate answers, and one whose path failed is a candidate ruled out in reasoning.',
  goal: 'Explored as a GOAL: first-ring branches are routes toward it or blockers across it, and one whose path failed is a route blocked, in reasoning.',
}
